import * as React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Check, CircleDashed, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label, NativeSelect, Textarea } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { BackLink } from "@/components/back-link";
import { DataTable } from "@/components/data-table";
import {
  ErrorState,
  KeyValues,
  LoadingRows,
  Mono,
  PageHeader,
  StatusBadge,
} from "@/components/common";
import { api } from "@/lib/api";
import { ago, dateTime, minor } from "@/lib/format";
import { label } from "@/lib/labels";

interface ProviderRow {
  id: string;
  type: string;
  status: string;
  displayName: string;
  slug: string | null;
  createdAt: string;
  updatedAt: string;
}

const columns: ColumnDef<ProviderRow, unknown>[] = [
  { accessorKey: "displayName", header: "Ad" },
  { accessorKey: "type", header: "Tür", cell: ({ row }) => label(row.original.type) },
  {
    accessorKey: "status",
    header: "Durum",
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  { accessorKey: "updatedAt", header: "Bekliyor", cell: ({ row }) => ago(row.original.updatedAt) },
];

export function ProvidersPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "pending_review";
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["admin-providers", status],
    queryFn: () => api<{ providers: ProviderRow[] }>(`/v1/admin/providers?status=${status}`),
  });
  return (
    <>
      <PageHeader
        title="Hizmet veren başvuruları"
        description="İncelemedeki başvurular en eskiden yeniye (flows.md §5)."
        actions={
          <NativeSelect
            aria-label="Durum"
            value={status}
            onChange={(e) => setParams({ status: e.target.value })}
          >
            {["pending_review", "active", "suspended", "rejected", "draft"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </NativeSelect>
        }
      />
      <DataTable
        columns={columns}
        data={q.data?.providers}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        onRowClick={(p) => navigate(`/providers/${p.id}`)}
        empty="Bekleyen başvuru yok."
      />
    </>
  );
}

interface ProviderView {
  id: string;
  type: string;
  status: string;
  displayName: string;
  slug: string | null;
  about: string | null;
  vatStatus: string | null;
  legal: {
    legalName: string | null;
    registrationNumber: string | null;
    vatNumber: string | null;
    taxId: string | null;
    taxResidenceCountry: string | null;
    dateOfBirth: string | null;
    dac7Consent: boolean;
    soleProprietorAck: boolean;
  };
  payoutConnected: boolean;
  members: {
    id: string;
    role: string;
    status: string;
    canTakeJobs: boolean;
    spokenLanguages: string[];
    areas: number;
    availabilityRules: number;
  }[];
  services: {
    code: string;
    pricingType: string;
    hourlyRateMinor: string | null;
    fixedPriceMinor: string | null;
    currency: string;
    isActive: boolean;
  }[];
  checklist: { items: { key: string; done: boolean; required: boolean }[]; ready: boolean };
}

const CHECK: Record<string, string> = {
  profile: "Profil (ad, tanıtım)",
  services: "En az bir hizmet",
  areas: "Hizmet bölgeleri",
  availability: "Müsaitlik",
  legal: "Yasal bilgiler",
  tax: "Vergi bilgileri (DAC7)",
  payout: "Stripe ödeme hesabı",
  identity: "Kimlik doğrulaması",
  conduct: "Sabıka kaydı",
};

export function ProviderDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [reason, setReason] = React.useState("");
  const q = useQuery({
    queryKey: ["admin-provider", id],
    queryFn: () => api<ProviderView>(`/v1/admin/providers/${id}`),
  });
  const back = <BackLink to="/providers">Başvurular</BackLink>;
  if (q.isPending)
    return (
      <>
        {back}
        <LoadingRows rows={8} />
      </>
    );
  if (q.error)
    return (
      <>
        {back}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );
  const p = q.data;
  const done = async () => {
    await qc.invalidateQueries({ queryKey: ["admin-providers"] });
    await qc.invalidateQueries({ queryKey: ["queue", "providers"] });
    navigate("/providers");
  };
  return (
    <>
      <PageHeader
        back={back}
        title={
          <span className="flex items-center gap-3">
            {p.displayName} <StatusBadge status={p.status} />
          </span>
        }
        description={
          <span>
            {label(p.type)} · <Mono>{p.id}</Mono>
          </span>
        }
        actions={
          p.status === "pending_review" && (
            <>
              <ActionDialog
                trigger={
                  <Button variant="outline">
                    <X /> Reddet
                  </Button>
                }
                title="Başvuruyu reddet"
                description="Gerekçe hizmet verene gönderilir; düzeltip yeniden başvurabilir."
                confirmLabel="Reddet"
                confirmVariant="destructive"
                canSubmit={reason.trim().length >= 3}
                success="Başvuru reddedildi."
                onSubmit={async (key) => {
                  await api(`/v1/admin/providers/${p.id}/reject`, {
                    method: "POST",
                    body: { reason: reason.trim() },
                    idempotencyKey: key,
                  });
                  await done();
                }}
              >
                <div className="grid gap-2">
                  <Label htmlFor="reason">Gerekçe</Label>
                  <Textarea
                    id="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={500}
                  />
                </div>
              </ActionDialog>
              <ActionDialog
                trigger={
                  <Button disabled={!p.checklist.ready}>
                    <Check /> Onayla
                  </Button>
                }
                title="Başvuruyu onayla"
                description={`${p.displayName} yayına alınacak ve iş almaya başlayabilecek.`}
                confirmLabel="Onayla"
                success="Hizmet veren onaylandı."
                onSubmit={async (key) => {
                  await api(`/v1/admin/providers/${p.id}/approve`, {
                    method: "POST",
                    idempotencyKey: key,
                  });
                  await done();
                }}
              />
            </>
          )
        }
      />
      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Kontrol listesi</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="grid gap-2 text-sm">
              {p.checklist.items.map((i) => (
                <li key={i.key} className="flex items-center gap-2">
                  {i.done ? (
                    <Check className="size-4 text-success" />
                  ) : (
                    <CircleDashed className="size-4 text-muted-foreground" />
                  )}
                  <span className={i.done ? "" : "text-muted-foreground"}>
                    {CHECK[i.key] ?? i.key}
                  </span>
                  {!i.required && <Badge variant="outline">isteğe bağlı</Badge>}
                </li>
              ))}
            </ul>
            {!p.checklist.ready && p.status === "pending_review" && (
              <p className="mt-3 text-xs text-muted-foreground">
                Zorunlu adımlar tamamlanmadan onay verilemez.
              </p>
            )}
          </CardContent>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Profil ve yasal bilgiler</CardTitle>
          </CardHeader>
          <CardContent>
            <KeyValues
              items={[
                ["Tanıtım", p.about ? <span className="whitespace-pre-line">{p.about}</span> : "—"],
                ["Yasal ad", p.legal.legalName],
                ["Sicil no", p.legal.registrationNumber],
                ["KDV durumu", p.vatStatus],
                ["KDV no", p.legal.vatNumber],
                ["Vergi no", p.legal.taxId],
                ["Vergi ülkesi", p.legal.taxResidenceCountry],
                ["Doğum tarihi", p.legal.dateOfBirth],
                ["DAC7 onayı", p.legal.dac7Consent ? "Var" : "Yok"],
                ["Stripe hesabı", p.payoutConnected ? "Bağlı" : "Bağlı değil"],
              ]}
            />
          </CardContent>
        </Card>
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base">Hizmetler ve ekip</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-6 lg:grid-cols-2">
            <ul className="divide-y text-sm">
              {p.services.map((s) => (
                <li key={s.code} className="flex items-center justify-between py-2">
                  <span className="flex items-center gap-2">
                    {s.code}
                    {!s.isActive && <Badge variant="outline">kapalı</Badge>}
                  </span>
                  <span className="tabular">
                    {s.pricingType === "hourly"
                      ? `${minor(s.hourlyRateMinor, s.currency)} / sa`
                      : minor(s.fixedPriceMinor, s.currency)}
                  </span>
                </li>
              ))}
              {!p.services.length && (
                <li className="py-2 text-muted-foreground">Hizmet eklenmemiş.</li>
              )}
            </ul>
            <ul className="divide-y text-sm">
              {p.members.map((m) => (
                <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="flex items-center gap-2">
                    {label(m.role)} <StatusBadge status={m.status} />
                  </span>
                  <span className="text-muted-foreground">
                    {m.areas} bölge · {m.availabilityRules} müsaitlik ·{" "}
                    {m.spokenLanguages.join(", ") || "dil yok"}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
      <p className="mt-4 text-xs text-muted-foreground">Son güncelleme {dateTime(new Date())}</p>
    </>
  );
}
